import { useEffect, useState } from 'react'
import { AxiosProgressEvent } from 'axios'

import uploadApi from 'app/axiosApi'
import { uploadActions } from 'app/slices/upload.slice'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { conversationActions } from 'app/slices/conversations.slice'
import { otrActions } from 'app/slices/otr.slice'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { t } from 'i18next'
import { ERRORS } from 'locales/locales'
import { MB } from 'utils/constants'

export default function useHandleFileQueue() {
  const [progress, setProgress] = useState<number | null>(null)
  const [fileName, setFileName] = useState('')
  const [hasUpload, setHasUpload] = useState(false)

  const dispatch = useAppDispatch()
  const { queue,queueParams } = useAppSelector((state) => state.upload)
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  const systemData = useAppSelector((state) => state.system.data)

  const onUploadProgress = async (event: AxiosProgressEvent) => {
    setProgress(Math.round((event.loaded * 100) / (event.total || 0)))
  }

  // ⬇️ Yardımcı temizleme fonksiyonu:
  // method'a göre (createMessage/createOtr/createThread) pending'i kaldırır.
  function cleanupPendingMessage(queueParams: any, dispatch: any) {
    const { method, roomId, cti } = queueParams || {}
    if (!cti) return
    if (method === 'createOtr') {
      // OTR pending mesaj kaldır
      dispatch(otrActions.removeOtrMessage({ roomId, cti }))
    } else if (method === 'createMessage') {
      // Normal mesaj pending kaldır (zaten cti'ye bakıyor)
      dispatch(conversationActions.removeFailedMessage({ roomId, cti }))
    } else if (method === 'createThread') {
      // Thread pending mesajı (UI local state ile ekleniyor) burada redux'tan kaldıramıyoruz.
      // NOT: Thread için pending ekleme component state ile (pendingHandler) yapıldığı için,
      // burada onu kaldıramıyoruz. İlk etapta message/OTR sorunsuz çalışsın diye bıraktım.
      // İyileştirme: pendingHandler'ı redux aksiyonu ile de ekleyip ortak kaldırma yapılabilir.
    }
  }

  const uploadFile = async () => {
    if (!queueParams) return
    
    const files: File[] = []
    
    try {
      setHasUpload(true)
      
      for(const data of queueParams?.content?.files || []){
        if (data.file.size > parseInt(systemData?.fileSizeLimit ?? 50) * MB) {
          throw new Error(
            t(ERRORS.FILE_SIZE_ERROR, {
              fileSize: systemData?.fileSizeLimit,
              fileSizeType: 'MB',
              file_name: data.file.name,
            }),
          )
        }
        setProgress(0)
        setFileName(data.file.name)
        files.push(data.file)
      }
 
      const res = await uploadApi.uploadFile(
        `files/uploads?from=room&method=${queueParams.method}&roomId=${queueParams?.roomId}`,
       files,
        'file',
        { params: queueParams },
        onUploadProgress,
        accessToken,
      )
  
      
      // ⬇️ Başarı koşulu: hem 200 hem de success===true olmalı
      const isOk = res?.status === 200 && res?.data?.success === true
      if (!isOk) {
        // Upload başarısız → pending mesajı kaldır
        cleanupPendingMessage(queueParams, dispatch)
        // Draft dosyalarını da temizle (mevcut davranışı koru)
        for (const data of queueParams?.content?.files || []) {
          dispatch(
            conversationActions.removeDataByUniqueIdInDraft({
              id: data.id,
              uniqueId: data.uniqueId,
            }),
          )
          dispatch(uploadActions.removeFromQueue({ uniqueId: data.uniqueId }))
        }
        throw new Error(res?.data?.message || 'Upload failed')
      }
      
      // ⬇️ Başarılı upload sonrası progress'i temizle
      setProgress(null)
      setFileName('')
      
    } catch (error: any) {
      console.warn('ERROR', error)
      
      // ⬇️ Hata durumunda da mutlaka pending kaldır
      if (queueParams) {
        cleanupPendingMessage(queueParams, dispatch)
      }
      
      // Draft dosyalarını temizle (mevcut)
      if (queueParams?.content?.files) {
        for(const data of queueParams.content.files) {
          dispatch(
            conversationActions.removeDataByUniqueIdInDraft({
              id: data.id,
              uniqueId: data.uniqueId,
            }),
          )
        }
      }
      
      let errorMessage = error.message || 'Upload failed'
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timeout. Please try again.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      DropdownAlert.ref.show({
        isError: true,
        message: errorMessage,
      })
    } finally {
      // Remove all files from queue and reset state
      if (queueParams?.content?.files) {
        for(const data of queueParams.content.files) {
          dispatch(uploadActions.removeFromQueue({ uniqueId: data.uniqueId }))
        }
      }
      setProgress(null)
      setFileName('')
      setHasUpload(false)
      dispatch(uploadActions.startQueue(null))
    }
  }

  useEffect(() => {
    if (!hasUpload && queueParams) {
      uploadFile()
    }
  }, [hasUpload,queueParams])

  return { progress, fileName }
}
